import type React from "react";
import type { HTMLAttributes } from "react";

import { HeadingProps } from "../../atoms/Heading";
import { HorizontalRule } from "../../atoms/HorizontalRule";
import { FirstHeading, HeadingWrapper, Root, SecondHeading } from "./styles";

export type HeadingAlignment = "left" | "right" | "center";

export type HeadingConfig = {
  /**
   * Visual size to display the heading at.
   */
  level?: HeadingProps["level"];
  /**
   * Content inside the heading.
   */
  content: string;
};

type DoubleHeadingProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Configuration for the first heading.
   */
  firstHeading: HeadingConfig;
  /**
   * Configuration for the second heading.
   */
  secondHeading: HeadingConfig;
  /**
   * Horizontal align both headings.
   */
  align?: HeadingAlignment;
  /**
   * Markup to generate (independent of the visual style).
   */
  as?: HeadingProps["as"];
};

/**
 * A heading with a subheading represented as a single title
 */
export function DoubleHeading({
  firstHeading,
  secondHeading,
  align = "left",
  as = "h1",
  ...rest
}: DoubleHeadingProps) {
  return (
    <Root data-testid={DoubleHeading.name} align={align} {...rest}>
      <HeadingWrapper as={as} align={align}>
        <FirstHeading lines={2} level={firstHeading.level ?? "h1"} as="span">
          {firstHeading.content}
        </FirstHeading>
        <HorizontalRule margin="none" />
        <SecondHeading lines={2} level={secondHeading.level ?? "h1"} as="span">
          {secondHeading.content}
        </SecondHeading>
      </HeadingWrapper>
    </Root>
  );
}
